import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./chatbot-dashboard.css";
import api from "../../../axiosInstance";
import {
  useActiveChats,
  useQueueChats,
  useMyChats,
  useChatThread,
  useClaimChat,
  useReleaseChat,
  useSendAgentMessage,
  useWhatsappDashboardSocket,
  useMediaBlob,
} from "../../../hooks/useChatbotQueries";
import {
  PageHeader,
  MetricCard,
  Spinner,
  SearchInput,
  EmptyState,
  ErrorState,
  Avatar,
  Badge,
  timeAgo,
} from "../../../Component/Workshop/UI";

const CHAT_TABS = [
  { key: "queue", label: "Needs Attention" },
  { key: "active", label: "All Active" },
  { key: "mine", label: "My Chats" },
];

const FLOW_LABELS = {
  SERVICE_SELECTION: "Choosing Service",
  ACCIDENT_REPORT: "Accident Report",
  QUICK_SERVICE_REQUEST: "Quick Service",
};

export default function ChatbotPage() {
  const navigate = useNavigate();
  useWhatsappDashboardSocket();

  const [activeTab, setActiveTab]     = useState("queue");
  const [search, setSearch]           = useState("");
  const [selectedId, setSelectedId]   = useState(null);
  const [draft, setDraft]             = useState("");
  const [listOpen, setListOpen]       = useState(true);   // collapses once a chat is opened
  const [detailsOpen, setDetailsOpen] = useState(false);  // drawer, off by default
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  const queueQuery = useQueueChats();
  const activeQuery = useActiveChats();
  const mineQuery = useMyChats();

  const listQuery = activeTab === "queue" ? queueQuery : activeTab === "active" ? activeQuery : mineQuery;
  const chats = listQuery.data || [];

  const filteredChats = useMemo(() => {
    if (!search.trim()) return chats;
    const q = search.trim().toLowerCase();
    return chats.filter(
      (c) =>
        c.phoneNumber?.toLowerCase().includes(q) ||
        c.caseNo?.toLowerCase().includes(q) ||
        c.lastMessagePreview?.toLowerCase().includes(q),
    );
  }, [chats, search]);

  const selectedChat = chats.find((c) => c.sessionId === selectedId) || null;

  const threadQuery = useChatThread(selectedId, dateFilter);
  const claimChat = useClaimChat();
  const releaseChat = useReleaseChat();
  const sendMessage = useSendAgentMessage();

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [threadQuery.data]);

  const session = threadQuery.data?.session || null;
  const messages = threadQuery.data?.messages || [];

  // isMine comes straight from the backend — never re-derive this by
  // comparing identity strings on the frontend.
  const isMine = session?.controlMode === "AGENT" && !!session?.isMine;
  const isClaimedByOther = session?.controlMode === "AGENT" && !isMine;

  const metrics = {
    active: activeQuery.data?.length ?? 0,
    queue:  queueQuery.data?.length ?? 0,
    mine:   mineQuery.data?.length ?? 0,
  };

  const handleSelect = (sessionId) => {
    setSelectedId(sessionId);
    setListOpen(false);   // give the conversation the room — reopen via the menu icon
    setDetailsOpen(false);
    setDateFilterOpen(false);
    setDateFilter(null);
  };

  const handleSend = () => {
    if (!draft.trim() || !selectedId) return;
    sendMessage.mutate({ sessionId: selectedId, text: draft.trim() });
    setDraft("");
  };

  const gridCols = `${listOpen ? "300px " : ""}1fr${detailsOpen ? " 280px" : ""}`;

  return (
    <div className="wd-animate cb-page" style={{ display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 100px)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <PageHeader title="WhatsApp Chatbot" subtitle="Live conversations, escalations, and human takeover" />
        <div className="cb-stat-strip">
          <StatChip label="Active" value={metrics.active} color="#3b82f6" />
          <StatChip label="Needs Attention" value={metrics.queue} color="#f97316" />
          <StatChip label="Claimed by You" value={metrics.mine} color="var(--cb-wa-green)" />
        </div>
      </div>

      <div className="cb-shell wd-card" style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: gridCols, overflow: "hidden", padding: 0, transition: "grid-template-columns .18s ease" }}>
        {/* ── Left: chat list (collapsible) ── */}
        {listOpen && (
          <div className="cb-list-pane">
            <div style={{ padding: 12, borderBottom: "1px solid var(--wd-border)", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {CHAT_TABS.map((t) => (
                  <button
                    key={t.key}
                    className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
                    style={{ fontSize: 12, padding: "6px 10px" }}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                    {t.key === "queue" && metrics.queue > 0 && <span className="cb-tab-count">{metrics.queue}</span>}
                  </button>
                ))}
              </div>
              <SearchInput value={search} onChange={setSearch} placeholder="Search phone, case, message..." />
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {listQuery.isLoading ? (
                <Spinner />
              ) : listQuery.isError ? (
                <ErrorState message="Failed to load chats" onRetry={listQuery.refetch} />
              ) : filteredChats.length === 0 ? (
                <EmptyState message={activeTab === "queue" ? "Nothing needs attention 🎉" : "No chats found"} />
              ) : (
                filteredChats.map((c) => (
                  <ChatListItem key={c.sessionId} chat={c} active={c.sessionId === selectedId} onClick={() => handleSelect(c.sessionId)} />
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Center: thread ── */}
        <div className="cb-thread-bg" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          {!selectedId ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--wd-t3)", fontSize: 13 }}>
              Select a conversation to view messages
            </div>
          ) : (
            <>
              <div className="cb-thread-header">
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <button className="cb-icon-btn" title={listOpen ? "Hide chat list" : "Show chat list"} onClick={() => setListOpen((v) => !v)}>
                    <MenuIcon />
                  </button>
                  <Avatar name={session?.phoneNumber || selectedChat?.phoneNumber || "?"} size={36} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, fontFamily: "var(--wd-mono)", color: "#fff" }}>
                      {session?.phoneNumber || selectedChat?.phoneNumber}
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.65)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {FLOW_LABELS[session?.currentFlow] || session?.currentFlow || "—"}
                      {session?.caseNo && (
                        <>
                          {" · "}
                          <span style={{ color: "#7cc0ff", cursor: "pointer" }} onClick={() => navigate(`/admin/cases/${session.caseNo}`)}>
                            {session.caseNo}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {session?.controlMode === "AGENT" ? (
                    isMine ? (
                      <button className="cb-btn-ghost-dark" disabled={releaseChat.isPending} onClick={() => releaseChat.mutate({ sessionId: selectedId })}>
                        {releaseChat.isPending ? "Releasing..." : "Release"}
                      </button>
                    ) : (
                      <Badge variant="orange">{session?.assignedAgentName || "Another agent"}</Badge>
                    )
                  ) : (
                    <button className="cb-btn-claim" disabled={claimChat.isPending} onClick={() => claimChat.mutate(selectedId)}>
                      {claimChat.isPending ? "Claiming..." : "Claim Chat"}
                    </button>
                  )}
                  <DateFilterControl
                    open={dateFilterOpen}
                    onToggle={() => setDateFilterOpen((v) => !v)}
                    dateFilter={dateFilter}
                    onApply={(range) => { setDateFilter(range); setDateFilterOpen(false); }}
                    onClear={() => { setDateFilter(null); setDateFilterOpen(false); }}
                  />
                  <button className="cb-icon-btn" title="Session details" onClick={() => setDetailsOpen((v) => !v)}>
                    <InfoIcon />
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="cb-messages" style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                  {dateFilter && (
                    <div className="cb-date-filter-banner">
                      Showing {formatDateLabel(dateFilter)}
                      <button onClick={() => setDateFilter(null)}>Back to live view</button>
                    </div>
                  )}
                      {threadQuery.isLoading ? <Spinner /> : messages.map((m) => <MessageBubble key={m.id} message={m} />)}
                    </div>

              <div className="cb-compose-bar">
                {isMine ? (
                  <>
                    <input
                      className="cb-compose-input"
                      placeholder="Type a message"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />
                    <button className="cb-send-btn" onClick={handleSend} disabled={!draft.trim() || sendMessage.isPending}>
                      <SendIcon />
                    </button>
                  </>
                ) : (
                  <div className="cb-claim-hint">
                    {isClaimedByOther
                      ? `This chat is being handled by ${session?.assignedAgentName || "another agent"}.`
                      : "Claim this chat to reply — the bot won't respond while you're connected."}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Right: details drawer (on demand) ── */}
        {detailsOpen && selectedChat && (
          <div className="cb-details-pane">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                Session Details
              </div>
              <button className="cb-icon-btn-light" onClick={() => setDetailsOpen(false)}><CloseIcon /></button>
            </div>
            <DetailRow label="Phone" value={selectedChat.phoneNumber} mono />
            <DetailRow label="Case No" value={selectedChat.caseNo || "—"} mono />
            <DetailRow label="Flow" value={FLOW_LABELS[selectedChat.currentFlow] || selectedChat.currentFlow} />
            <DetailRow label="Step" value={selectedChat.currentStep || "—"} />
            <DetailRow label="Mode" value={selectedChat.controlMode === "AGENT" ? "Agent" : "Bot"} />
            <DetailRow label="Last Activity" value={selectedChat.lastMessageAt ? timeAgo(selectedChat.lastMessageAt) : "—"} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 * SUBCOMPONENTS
 * ============================================================================ */

function StatChip({ label, value, color }) {
  return (
    <div className="cb-chip">
      <span className="cb-chip-dot" style={{ background: color }} />
      <span className="cb-chip-value" style={{ color }}>{value}</span>
      <span className="cb-chip-label">{label}</span>
    </div>
  );
}

function ChatListItem({ chat, active, onClick }) {
  const needsAgent = chat.sessionStatus === "ABANDONED" || chat.currentStep === "HUMAN_SUPPORT_REQUIRED";
  return (
    <div className={`cb-list-item ${active ? "active" : ""}`} onClick={onClick}>
      <Avatar name={chat.phoneNumber || "?"} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13, fontFamily: "var(--wd-mono)" }}>{chat.phoneNumber}</span>
          <span style={{ fontSize: 10, color: "var(--wd-t3)", whiteSpace: "nowrap" }}>{chat.lastMessageAt ? timeAgo(chat.lastMessageAt) : ""}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--wd-t3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {chat.lastMessagePreview || "—"}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
          <span className={`cb-mode-dot ${chat.controlMode === "AGENT" ? "agent" : "bot"}`} />
          <span style={{ fontSize: 10, color: "var(--wd-t3)" }}>
            {chat.controlMode === "AGENT" ? (chat.isMine ? "You're handling" : "Agent handling") : "Bot handling"}
          </span>
          {needsAgent && <span className="wd-badge wd-badge-orange" style={{ fontSize: 9 }}>Needs Agent</span>}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isAgent = message.direction === "OUTGOING" && message.metadata?.agentId;
  const isSystem = message.messageType === "SYSTEM" || message.metadata?.systemNotice;
  const isIncoming = message.direction === "INCOMING";
  const mediaId = message.metadata?.mediaId;

  if (isSystem) {
    return <div className="cb-system-pill">{message.content}</div>;
  }

  return (
    <div className={`cb-bubble-row ${isIncoming ? "in" : "out"}`}>
      <div className={`cb-bubble ${isIncoming ? "cb-bubble-in" : isAgent ? "cb-bubble-agent" : "cb-bubble-bot"}`}>
        {!isIncoming && <div className="cb-bubble-tag">{isAgent ? "Agent" : "Bot"}</div>}

        {mediaId && message.messageType === "IMAGE" ? (
          <MediaImage mediaId={mediaId} caption={message.metadata?.caption} />
        ) : mediaId ? (
          <MediaAttachmentChip mediaId={mediaId} messageType={message.messageType} filename={message.metadata?.filename} />
        ) : (
          <div className="cb-bubble-text">{message.content}</div>
        )}

        <div className="cb-bubble-time">
          {message.createdAt ? new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
          {!isIncoming && <DeliveryTicks status={message.deliveryStatus} />}
        </div>
      </div>
    </div>
  );
}

function DateFilterControl({ open, onToggle, dateFilter, onApply, onClear }) {
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(dateFilter?.fromDate || todayISO());
  const [to, setTo] = useState(dateFilter?.toDate || todayISO());

  // Keep local draft in sync if the filter is cleared/changed elsewhere (e.g. chat switch)
  useEffect(() => {
    setFrom(dateFilter?.fromDate || todayISO());
    setTo(dateFilter?.toDate || todayISO());
  }, [dateFilter]);

  const applySingleDay = (isoDate) => onApply({ fromDate: isoDate, toDate: isoDate });

  return (
    <div style={{ position: "relative" }}>
      <button
        className={`cb-icon-btn ${dateFilter ? "cb-icon-btn-active" : ""}`}
        title={dateFilter ? "Viewing a specific date" : "Filter by date"}
        onClick={onToggle}
      >
        <CalendarIcon />
      </button>

      {open && (
        <div className="cb-date-popover">
          <div className="cb-date-popover-quick">
            <button onClick={() => applySingleDay(todayISO())}>Today</button>
            <button
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 1);
                applySingleDay(d.toISOString().slice(0, 10));
              }}
            >
              Yesterday
            </button>
          </div>

          <div className="cb-date-popover-range">
            <label>
              From
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={to} min={from} max={todayISO()} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>

          <div className="cb-date-popover-actions">
            <button className="cb-btn-ghost-dark" onClick={onClear} disabled={!dateFilter}>
              Clear (back to live)
            </button>
            <button className="cb-btn-claim" onClick={() => onApply({ fromDate: from, toDate: to })}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDateLabel({ fromDate, toDate }) {
  if (fromDate === toDate) {
    return new Date(fromDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  return `${fromDate} – ${toDate}`;
}

// Eagerly loads — images are the common case and worth previewing inline.
// Fetched as an authenticated blob (see useMediaBlob) since the media proxy
// route needs the JWT header a plain <img src> can't send.
function MediaImage({ mediaId, caption }) {
  const { data: blob, isLoading, isError } = useMediaBlob(mediaId);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (isLoading) return <div className="cb-media-loading">Loading photo…</div>;
  if (isError || !url) return <div className="cb-media-error">📷 Photo unavailable</div>;

  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={caption || "Photo"} className="cb-media-image" />
    </a>
  );
}

// Documents/audio/video — fetched lazily on click rather than preloaded,
// since these are opened far less often than images are previewed.
function MediaAttachmentChip({ mediaId, messageType, filename }) {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/whatsapp/dashboard/media/${mediaId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      // swallow — button just stays clickable for retry
    } finally {
      setLoading(false);
    }
  };

  const icon = messageType === "DOCUMENT" ? "📄" : messageType === "AUDIO" ? "🎤" : messageType === "VIDEO" ? "🎥" : "📎";

  return (
    <button className="cb-media-chip" onClick={handleOpen} disabled={loading}>
      {icon} {loading ? "Opening..." : filename || `View ${messageType?.toLowerCase() || "attachment"}`}
    </button>
  );
}

// Real delivery status from Meta — SENT (1 tick), DELIVERED (2 gray),
// READ (2 blue), FAILED (red !). Never fabricated presence/online data.
function DeliveryTicks({ status }) {
  if (status === "FAILED") return <span className="cb-tick cb-tick-failed" title="Failed">!</span>;
  if (status === "READ") return <span className="cb-tick cb-tick-read">✓✓</span>;
  if (status === "DELIVERED") return <span className="cb-tick cb-tick-delivered">✓✓</span>;
  if (status === "SENT") return <span className="cb-tick cb-tick-sent">✓</span>;
  return null;
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--wd-t1)", fontFamily: mono ? "var(--wd-mono)" : "inherit", marginTop: 2 }}>{value}</div>
    </div>
  );
}

/* ── Icons ─────────────────────────────────────────────────────────────── */
function MenuIcon()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></svg>; }
function InfoIcon()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>; }
function CloseIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function SendIcon()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>; }
function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}